import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, IdentificationIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { getRiderDetails, updateRiderNinVerification } from "../api/ridersApi";
import { extractApiError } from "@/lib/api-error";
import { usePermissions } from "@/features/auth/usePermissions";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import type { RiderDetails } from "../types";

const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const StatusBadge = ({ status }: { status: string }) => {
    const styles: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        INACTIVE: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        DELETED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        BANNED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    };

    return (
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {status?.replace(/_/g, " ")}
        </span>
    );
};

const InlineError = ({ message }: { message: string }) =>
    message ? (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
        </div>
    ) : null;

export const ManualNinVerificationRoute = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const canModerateRider = hasPermission(SECTION_PERMISSIONS.USERS_MODERATE[0]);

    const [nin, setNin] = useState("");
    const [adminNotes, setAdminNotes] = useState("");
    const [verified, setVerified] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [formError, setFormError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["riderDetails", userId],
        queryFn: () => getRiderDetails(userId!),
        enabled: !!userId,
    });

    const rider = data?.data as RiderDetails | undefined;

    useEffect(() => {
        if (!rider) return;
        setNin(rider.nin ?? "");
        setVerified(rider.ninVerified || rider.ninData?.verified || false);
    }, [rider]);

    const mutation = useMutation({
        mutationFn: () =>
            updateRiderNinVerification(userId!, {
                nin: nin.trim(),
                verified,
                adminNotes: adminNotes.trim() || undefined,
                file: file as File,
            }),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["riderDetails", userId] });
            queryClient.invalidateQueries({ queryKey: ["riders"] });
            navigate(`/riders/${userId}`, { replace: false });
            queryClient.setQueryData(["riderDetails", userId], (prev: any) =>
                prev ? { ...prev, data: { ...prev.data, ...response.data } } : prev
            );
        },
        onError: (err) => {
            setFormError(extractApiError(err));
        },
    });

    const pageTitle = useMemo(() =>
        rider ? `${rider.firstName} ${rider.lastName}` : "Manual NIN verification",
    [rider]);

    if (!canModerateRider) {
        return (
            <div className="flex h-96 items-center justify-center text-sm text-slate-600 dark:text-slate-400">
                You do not have permission to review rider identity verification.
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading rider details...</p>
                </div>
            </div>
        );
    }

    if (error || !rider) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">Failed to load rider</p>
                    <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">Please try again later.</p>
                    <button
                        onClick={() => navigate("/riders")}
                        className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
                    >
                        Back to Riders
                    </button>
                </div>
            </div>
        );
    }

    const submit = () => {
        const trimmedNin = nin.trim();

        if (!trimmedNin) {
            setFormError("NIN is required.");
            return;
        }

        if (!/^\d{11}$/.test(trimmedNin)) {
            setFormError("NIN must be exactly 11 numeric digits.");
            return;
        }

        if (!file) {
            setFormError("NIN document file is required.");
            return;
        }

        mutation.mutate();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate(`/riders/${userId}`)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                    Back to rider profile
                </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-r from-violet-500 to-indigo-600 px-8 py-6">
                    <div className="flex items-center gap-5">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-2xl font-bold text-white shadow-lg">
                            {rider.firstName[0]}{rider.lastName[0]}
                        </div>
                        <div className="flex-1">
                            <div className="mb-2 flex items-center gap-3">
                                <h1 className="text-2xl font-bold text-white">{pageTitle}</h1>
                                <StatusBadge status={rider.status} />
                            </div>
                            <p className="text-sm text-violet-100">User ID: {rider.id}</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                        <div className="mb-2 flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <UserCircleIcon className="h-4 w-4" />
                            <span className="text-xs font-medium uppercase tracking-wide">Name</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{rider.firstName} {rider.lastName}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                        <div className="mb-2 flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <IdentificationIcon className="h-4 w-4" />
                            <span className="text-xs font-medium uppercase tracking-wide">Email</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{rider.email}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                        <div className="mb-2 flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.1 5.18 2 2 0 0 1 5.09 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.62a2 2 0 0 1-.45 2.11L9 10.91a16 16 0 0 0 6.1 6.1l1.46-1.26a2 2 0 0 1 2.11-.45c.84.29 1.72.5 2.62.62A2 2 0 0 1 22 16.92Z"/>
                            </svg>
                            <span className="text-xs font-medium uppercase tracking-wide">Phone</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{rider.phoneNumber}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                        <div className="mb-2 flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <CheckCircleIcon className="h-4 w-4" />
                            <span className="text-xs font-medium uppercase tracking-wide">Joined</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(rider.createdAt)}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="mb-5 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
                            <IdentificationIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Manual NIN review</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Review the rider’s identity document before confirming eligibility.</p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">NIN Number</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={nin}
                                onChange={(e) => {
                                    setNin(e.target.value.replace(/\D/g, "").slice(0, 11));
                                    if (formError) setFormError("");
                                }}
                                placeholder="Enter 11-digit NIN"
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            />
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">NIN Document File</label>
                            <input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) => {
                                    const selectedFile = e.target.files?.[0] ?? null;
                                    setFile(selectedFile);
                                    if (formError) setFormError("");
                                }}
                                className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm file:mr-3 file:rounded-md file:border-0 file:bg-violet-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-violet-700 hover:file:bg-violet-200 focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:file:bg-violet-900/30 dark:file:text-violet-300"
                            />
                            {file && (
                                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Selected file: {file.name}</p>
                            )}
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Admin Notes <span className="text-xs text-slate-400">(optional)</span></label>
                            <textarea
                                rows={4}
                                value={adminNotes}
                                onChange={(e) => {
                                    setAdminNotes(e.target.value);
                                    if (formError) setFormError("");
                                }}
                                placeholder="Reviewed against original NIN slip provided by rider."
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            />
                        </div>

                        <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/40">
                            <input
                                type="checkbox"
                                checked={verified}
                                onChange={(e) => setVerified(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                            />
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Verify this rider and grant ride eligibility</span>
                        </label>

                        <InlineError message={formError} />

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => navigate(`/riders/${userId}`)}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submit}
                                disabled={mutation.isPending}
                                className="flex-1 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-violet-700 disabled:opacity-50"
                            >
                                {mutation.isPending ? "Saving..." : "Save Verification"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-800/40 xl:col-span-1">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Current status</h3>
                    <div className="mt-5 space-y-4 text-sm">
                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                            <span className="text-slate-500 dark:text-slate-400">NIN verified</span>
                            <span className={`font-semibold ${rider.ninVerified ? "text-emerald-600" : "text-slate-500"}`}>
                                {rider.ninVerified ? "Yes" : "No"}
                            </span>
                        </div>
                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                            <span className="text-slate-500 dark:text-slate-400">ID verified</span>
                            <span className={`font-semibold ${rider.isIdVerified ? "text-emerald-600" : "text-slate-500"}`}>
                                {rider.isIdVerified ? "Yes" : "No"}
                            </span>
                        </div>
                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                            <span className="text-slate-500 dark:text-slate-400">Liveness checked</span>
                            <span className={`font-semibold ${rider.livenessChecked ? "text-emerald-600" : "text-slate-500"}`}>
                                {rider.livenessChecked ? "Yes" : "No"}
                            </span>
                        </div>
                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                            <span className="text-slate-500 dark:text-slate-400">Ride eligibility</span>
                            <span className={`font-semibold ${rider.canPerformRides ? "text-emerald-600" : "text-slate-500"}`}>
                                {rider.canPerformRides ? "Allowed" : "Restricted"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
